import type { RefundDecision } from '../api/supportApi'

/** What pressing Approve on a refund will do, in the words the button and dialog use. */
export function refundApproveText(d: RefundDecision): { button: string; confirm: string; warning?: string } {
  const o = d.order
  const amount = o ? `${o.total} ${o.currency}` : ''
  switch (d.plan) {
    case 'cancel_and_refund':
      return {
        button: `Approve: cancel and refund ${amount}`,
        confirm: `Stop the print at Prodigi${d.print ? '' : ' (not sent yet)'}, cancel ${o?.name} in Shopify and refund ${amount} to the original payment method.`,
      }
    case 'printing':
      return {
        button: `Approve: cancel and refund ${amount}`,
        confirm: `Prodigi has already started printing ${o?.name}. We will try to cancel the print, then cancel and refund ${amount}, even if the print cannot be stopped.`,
        warning: 'Already printing: the print may still be made and shipped.',
      }
    case 'shipped':
      return {
        button: 'Approve refund on return',
        confirm: `${o?.name} has already shipped, so nothing is cancelled now. The customer is told the refund of ${amount} follows once the print is returned. Refund it in Shopify when it arrives.`,
        warning: 'Already shipped: refund by hand in Shopify once it is returned.',
      }
    case 'manual':
      return {
        button: 'Approve: stop the print',
        confirm: `Stop the print at Prodigi. This app cannot refund orders in Shopify, so refund ${amount} for ${o?.name} by hand there.`,
        warning:
          d.refundAccess === 'missing_write_orders'
            ? 'Refund by hand: the Shopify app needs the write_orders permission to refund from here.'
            : `Refund by hand: could not check the Shopify permission (${(d.refundAccess || '').replace(/^unknown: /, '')}).`,
      }
    case 'already_refunded':
      return { button: 'Approve (already refunded)', confirm: `${o?.name} is already cancelled and refunded. Nothing more is done; the customer is told.` }
    default:
      return { button: 'Approve', confirm: '' }
  }
}

/** Prodigi's progress, for the facts table. */
export function printStatus(d: RefundDecision): string {
  const p = d.print
  if (!p) return 'Not sent to Prodigi yet'
  if ('error' in p) return `Could not check (${p.error})`
  if (p.stage === 'Cancelled') return 'Cancelled at Prodigi'
  if (p.shipping) return 'Shipped by Prodigi'
  if (p.printing) return 'Printing at Prodigi'
  return `At Prodigi, not printing yet (${p.id})`
}
